import { Anchor, Button, Input, Modal, Toast } from '@byecode/ui'
import { CopyButton, ErrorMessage, TagIcon, useAtomAction, useAtomData } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useAsyncFn } from 'react-use'

import { fetchOtherPlatformsAtom } from '@/atoms/application/action'
import { otherPlatformListAtom } from '@/atoms/application/state'
import {
    otherPlatformBackgroundColorMap,
    otherPlatformColorMap,
    otherPlatformDescribeMap,
    otherPlatformIconMap,
    otherPlatformNameMap
} from '@/containers/OtherPlatforms/constant'
import * as srv from '@/services'

import { PLACEHOLDER_PASSWORD } from '../constants'
import * as SC from '../styles'
import type { DeepSeekFormState } from './type'

interface DeepSeekAccountProps {
    onClose?: () => void
}

const initData: DeepSeekFormState = {
    id: '',
    apiKey: ''
}
const DeepSeekAccount: React.FunctionComponent<DeepSeekAccountProps> = ({ onClose }) => {
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    const { run: fetchOtherPlatforms } = useAtomAction(fetchOtherPlatformsAtom)

    const account = useMemo(() => {
        const otherPlatform = find(item => item.type === 'DEEP_SEEK', otherPlatforms ?? [])
        return otherPlatform?.configurationComplete
            ? { ...otherPlatform?.config, id: otherPlatform.id, apiKey: PLACEHOLDER_PASSWORD }
            : { ...initData, id: otherPlatform?.id }
    }, [otherPlatforms])

    const {
        control,
        register,
        handleSubmit,
        getValues,
        formState: { errors }
    } = useForm({
        mode: 'onSubmit',
        defaultValues: account
    })

    const [{ loading: submitLoading }, doUpdateIntegration] = useAsyncFn(srv.updateIntegration)

    const handleUpdate = useCallback(
        async (params: DeepSeekFormState) => {
            const { id, apiKey } = params
            if (!id || submitLoading) {
                return
            }
            const res = await doUpdateIntegration({
                type: 'DEEP_SEEK',
                id,
                config: {
                    apiKey: apiKey === PLACEHOLDER_PASSWORD ? undefined : apiKey
                }
            })
            if (res) {
                fetchOtherPlatforms()
                onClose?.()
            }
        },
        [onClose, submitLoading, fetchOtherPlatforms, doUpdateIntegration]
    )

    const handleInviteSubmit = useCallback(
        (ev: React.FormEvent) => {
            ev.preventDefault()
            handleSubmit(handleUpdate)()
        },
        [handleSubmit, handleUpdate]
    )

    return (
        <SC.Container onSubmit={handleInviteSubmit}>
            <SC.Content>
                <TagIcon
                    size={48}
                    iconSize={32}
                    icon={otherPlatformIconMap['DEEP_SEEK']}
                    radius={6}
                    iconColor={otherPlatformColorMap['DEEP_SEEK']}
                    background={otherPlatformBackgroundColorMap['DEEP_SEEK']}
                />
                <SC.Title>{otherPlatformNameMap['DEEP_SEEK']}</SC.Title>
                <SC.Item>
                    <SC.Description>{otherPlatformDescribeMap['DEEP_SEEK']}</SC.Description>
                </SC.Item>
                <SC.Tip>
                    <SC.Icon color="var(--color-main)" type="WarningCircle" />
                    <SC.Text>如果您完善授权时遇到问题，请查看帮助文档：</SC.Text>
                    <Anchor
                        target="_blank"
                        style={{ fontWeight: 600, color: 'var(--color-main)' }}
                        to="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/ox73417kvduroypv?singleDoc#"
                    >
                        DeepSeek授权配置
                    </Anchor>
                </SC.Tip>

                <SC.Item>
                    <SC.Text>
                        API Key
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="apiKey" errors={errors}>
                        <Controller
                            name="apiKey"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        size="lg"
                                        autoComplete="new-password"
                                        type="password"
                                        placeholder="请填入API Key"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim()) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                    <SC.Description>
                        <Anchor target="_blank" to="https://platform.deepseek.com/api_keys" style={{ color: 'var(--color-main)' }}>
                            如何获取？
                        </Anchor>
                    </SC.Description>
                </SC.Item>
            </SC.Content>
            <SC.Footer>
                <SC.LeftFill />
                <SC.RightFill>
                    <Button size="lg" style={{ width: 104, height: 40 }} onClick={() => onClose?.()}>
                        取消
                    </Button>
                    <Button size="lg" loading={submitLoading} style={{ width: 104, height: 40 }} type="primary" htmlType="submit">
                        确定
                    </Button>
                </SC.RightFill>
            </SC.Footer>
        </SC.Container>
    )
}

export default DeepSeekAccount
